import { createColumnHelper } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { triggersQuery } from 'components/domain/automation/triggers/triggersQuery';
import useFilterFormContext from 'components/filters/useFilterFormContext';
import CommonTable from 'components/table/CommonTable';
import useServerFilteredTable from 'components/table/hooks/useServerFilteredTable';
import { Trigger } from 'models/automation/Triggers';
import { TriggersTabFiltersSchemaType } from '../filters/TriggersTabFilters.schema';
import useSearchedTrigger from '../filters/useSearchedTrigger';
import ActionTypeCell from './cells/ActionTypeCell';
import ActionsCell from './cells/ActionsCell';
import RecipientsCell from './cells/RecipientsCell';
import TriggerTypeCell from './cells/TriggerTypeCell';

const TriggersTable = () => {
  const { t } = useTranslation();
  const searchTerm = useSearchedTrigger();
  const { lastSubmittedFilters } =
    useFilterFormContext<TriggersTabFiltersSchemaType>();
  const columnHelper = createColumnHelper<Trigger>();

  const columns = [
    columnHelper.accessor('name', {
      header: t('common.name'),
      sortDescFirst: true,
    }),
    columnHelper.accessor('triggerType', {
      header: t('pageAutomations.triggers.table.triggerType'),
      cell: TriggerTypeCell,
      enableSorting: false,
    }),
    columnHelper.accessor('actionType', {
      header: t('pageAutomations.triggers.table.actionType'),
      cell: ActionTypeCell,
      enableSorting: false,
    }),
    columnHelper.accessor('recipients', {
      header: t('pageAutomations.triggers.table.recipients'),
      cell: RecipientsCell,
      enableSorting: false,
    }),
    columnHelper.accessor('templateName', {
      header: t('pageAutomations.triggers.table.template'),
      enableSorting: false,
    }),
    columnHelper.display({
      id: 'actions',
      header: t('common.actions'),
      cell: ActionsCell,
      meta: {
        showCellTooltip: false,
      },
    }),
  ];

  const { tableInstance, isLoading } = useServerFilteredTable({
    columns,
    getQuery: (options) =>
      triggersQuery({
        filters: { ...lastSubmittedFilters },
        searchTerm,
        ...options,
      }),
    pageSize: 10,
  });

  return (
    <CommonTable
      data-testid="triggers-table"
      emptyState={t('pageAutomations.triggers.emptyTable')}
      isLoading={isLoading}
      tableInstance={tableInstance}
    />
  );
};

export default TriggersTable;
